import React, { useState, useMemo } from 'react';

/**
 * Componente para selección múltiple de cargos específicos
 * Diseño moderno integrado con Step1 UX/UI
 * Incluye funcionalidad de "Seleccionar Todos" y manejo de estados de carga
 * 
 * @version 2.1.0 - Busca y Filtra Cargos
 */
const CargosMultiselect = ({
  formData,
  errors,
  cargosEspecificos,
  loadingCargos,
  handleCargoChange,
  handleMarcarTodosCargos
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar cargos cuando hay término de búsqueda
  const cargosMostrados = useMemo(() => {
    if (!searchTerm) return cargosEspecificos;
    return cargosEspecificos.filter(cargo =>
      cargo.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [cargosEspecificos, searchTerm]);

  return (
    <div className="step1-cargos-container">
      {/* Label con ícono y Buscador Integrado */}
      <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '8px',
          flexWrap: 'wrap',
          gap: '8px'
      }}>
          <label className="step1-cargos-label" style={{ marginBottom: 0 }}>
            <span className="step1-cargos-label-icon">👥</span>
            Cargos Específicos
            {!formData.idTipoCargo && (
              <span className="step1-cargos-hint">
                (Seleccione primero un tipo de cargo)
              </span>
            )}
          </label>
          
          {/* Barra de búsqueda si hay cargos */}
          {cargosEspecificos.length > 0 && !loadingCargos && formData.idTipoCargo && (
              <div className="step1-cargos-search" style={{ position: 'relative', width: '220px' }}>
                  <input
                      type="text"
                      placeholder="Buscar cargo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                          width: '100%',
                          padding: '6px 30px 6px 10px',
                          fontSize: '13px',
                          fontWeight: '400',
                          borderRadius: '6px',
                          border: '1px solid #ced4da',
                          height: '32px',
                          outline: 'none',
                          backgroundColor: '#fff',
                          transition: 'border-color 0.2s, box-shadow 0.2s'
                      }}
                      onFocus={(e) => {
                          e.target.style.borderColor = '#0063a1';
                          e.target.style.boxShadow = '0 0 0 2px rgba(0, 99, 161, 0.1)';
                      }}
                      onBlur={(e) => {
                          e.target.style.borderColor = '#ced4da';
                          e.target.style.boxShadow = 'none';
                      }}
                  />
                  <i className="fas fa-search" style={{ 
                      position: 'absolute', 
                      right: searchTerm ? '30px' : '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      fontSize: '12px',
                      color: '#adb5bd',
                      pointerEvents: 'none'
                  }}></i>
                  {searchTerm && (
                      <i 
                          className="fas fa-times"
                          onClick={() => setSearchTerm('')}
                          style={{ 
                              position: 'absolute', 
                              right: '8px', 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              fontSize: '13px',
                              color: '#adb5bd',
                              cursor: 'pointer',
                              padding: '4px',
                              transition: 'color 0.2s'
                          }}
                          title="Limpiar búsqueda"
                          onMouseEnter={(e) => e.target.style.color = '#495057'}
                          onMouseLeave={(e) => e.target.style.color = '#adb5bd'}
                      ></i>
                  )}
              </div>
          )}
      </div>
      
      {/* Lista de cargos */}
      <div className={`step1-cargos-list ${!formData.idTipoCargo || loadingCargos ? 'disabled' : ''}`}>
        {/* Estado: Sin tipo de cargo seleccionado */}
        {!formData.idTipoCargo ? (
          <div className="step1-empty-state">
            <div className="step1-empty-icon">📋</div>
            <div className="step1-empty-text">
              Seleccione un tipo de cargo para ver los cargos disponibles
            </div>
          </div>
        ) : loadingCargos ? (
          <React.Fragment>
            {/* Estado: Cargando */}
            <div className="step1-empty-state">
              <div className="step1-loading-spinner"></div>
              <div className="step1-empty-text" style={{ marginTop: '12px' }}>
                Cargando cargos disponibles...
              </div>
            </div>
          </React.Fragment>
        ) : cargosEspecificos.length === 0 ? (
          <React.Fragment>
            {/* Estado: Sin cargos disponibles */}
            <div className="step1-empty-state">
              <div className="step1-empty-icon">🔍</div>
              <div className="step1-empty-text">
                No hay cargos disponibles para este tipo
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment key={`cargos-list-${formData.idTipoCargo || 'none'}`}>
            {/* Estado: Lista de cargos disponibles */}
            
            {/* Checkbox "Seleccionar Todos" - Solo visible si NO hay búsqueda activa */}
            {!searchTerm && (
                <div className="step1-select-all-item">
                <label className="step1-cargo-label step1-select-all-label">
                    <input
                    type="checkbox"
                    checked={formData.cargosSeleccionados.length === cargosEspecificos.length && cargosEspecificos.length > 0}
                    onChange={handleMarcarTodosCargos}
                    className="step1-cargo-checkbox"
                    />
                    ✅ Seleccionar Todos <span className="step1-cargo-count">({cargosEspecificos.length} cargos)</span>
                </label>
                </div>
            )}
            
            {/* Si hay búsqueda y no hay resultados */}
            {searchTerm && cargosMostrados.length === 0 && (
                <div className="step1-empty-state" style={{ padding: '20px' }}>
                    <div className="step1-empty-text">
                        No se encontraron cargos para "{searchTerm}"
                    </div>
                </div>
            )}

            {/* Lista individual de cargos */}
            {cargosMostrados.map((cargo) => (
              <div key={cargo.value} className="step1-cargo-item">
                <label className="step1-cargo-label">
                  <input
                    type="checkbox"
                    checked={formData.cargosSeleccionados.includes(cargo.value)}
                    onChange={() => handleCargoChange(cargo.value)}
                    className="step1-cargo-checkbox"
                  />
                  {/* Resalta coincidencias si hay busqueda */}
                  {searchTerm ? (
                      <span dangerouslySetInnerHTML={{
                          __html: cargo.label.replace(
                              new RegExp(`(${searchTerm})`, 'gi'),
                              (match) => `<mark style="background-color: #ffeba8; padding: 0; color: inherit;">${match}</mark>`
                          )
                      }} />
                  ) : (
                      cargo.label
                  )}
                </label>
              </div>
            ))}
          </React.Fragment>
        )}
      </div>
      
      {/* Mensaje de error */}
      {errors.cargosSeleccionados && (
        <div className="step1-validation-message">
          <span className="step1-validation-icon">⚠️</span>
          {errors.cargosSeleccionados}
        </div>
      )}
    </div>
  );
};

export default CargosMultiselect;